import { FilterMetadata } from '@/api/generatedApi';
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { getMetadataColumns, getMetadataColumnsFromFilterData } from '@/utils/metadata';
import { useListAllMetadataFields } from './api/metadata/useListAllMetadataFields';

const extractFieldName = (field: string): string => field.replace(/^metadata\./, '');

const getColumnOptions = (canEditMetadata: boolean, filterable?: boolean, onApplyFilter?: () => void) => ({
  editable: canEditMetadata,
  filterable,
  onApplyFilter,
});

export const useMetadataColumnVisibility = ({
  metadataFieldNames,
  isLoadingFieldNames,
  canEditMetadata,
  contextKey,
  filterable,
  filterDataMetadata,
  onApplyFilter,
}: {
  metadataFieldNames: string[];
  isLoadingFieldNames: boolean;
  canEditMetadata: boolean;
  contextKey: string;
  filterable?: boolean;
  filterDataMetadata?: FilterMetadata;
  onApplyFilter?: () => void;
}) => {
  const { metadataFields, isLoading: isLoadingMetadataFields } = useListAllMetadataFields();
  const [userVisibilityState, setUserVisibilityState] = useState<{
    contextKey: string;
    model: GridColumnVisibilityModel;
  }>();
  const isMetadataLoading = isLoadingMetadataFields || isLoadingFieldNames;

  const metadataColumns = useMemo(() => {
    const columnOptions = getColumnOptions(canEditMetadata, filterable, onApplyFilter);
    const allMetadataColumns = getMetadataColumns({ metadataFields, ...columnOptions });
    const filterDataColumns = getMetadataColumnsFromFilterData({
      filterDataMetadata,
      metadataFields,
      ...columnOptions,
    });

    const filterDataFieldNames = new Set(filterDataColumns.map(col => col.field));
    const mergedColumns = [
      ...filterDataColumns,
      ...allMetadataColumns.filter(col => !filterDataFieldNames.has(col.field)),
    ];

    const visibleFieldNames = new Set(metadataFieldNames);
    const [visibleColumns, hiddenColumns] = mergedColumns.reduce<[typeof mergedColumns, typeof mergedColumns]>(
      ([visible, hidden], col) => {
        const fieldName = extractFieldName(col.field);
        return visibleFieldNames.has(fieldName) ? [[...visible, col], hidden] : [visible, [...hidden, col]];
      },
      [[], []]
    );

    return [...visibleColumns, ...hiddenColumns].sort(
      (a, b) => (a?.headerName ?? '').localeCompare(b?.headerName ?? '', undefined, { sensitivity: 'base' }) ?? 0
    );
  }, [metadataFieldNames, metadataFields, canEditMetadata, filterable, filterDataMetadata, onApplyFilter]);

  const derivedVisibilityModel = useMemo(() => {
    const allMetadataColumns = getMetadataColumns({ metadataFields, editable: canEditMetadata });
    const visibleFieldNames = new Set(metadataFieldNames);
    const visibilityModel: Record<string, boolean> = { extension: false, state: false };

    for (const col of allMetadataColumns) {
      if (!visibleFieldNames.has(extractFieldName(col.field))) {
        visibilityModel[col.field] = false;
      }
    }

    if (isMetadataLoading) {
      visibilityModel.creator = false;
      visibilityModel.createdAt = false;
      visibilityModel.folder = false;
    }

    return visibilityModel;
  }, [metadataFieldNames, metadataFields, canEditMetadata, isMetadataLoading]);

  const columnVisibilityModel = useMemo(
    () => (userVisibilityState?.contextKey === contextKey ? userVisibilityState.model : derivedVisibilityModel),
    [derivedVisibilityModel, userVisibilityState, contextKey]
  );

  return {
    metadataColumns,
    columnVisibilityModel,
    onColumnVisibilityModelChange: (newModel: GridColumnVisibilityModel) =>
      setUserVisibilityState({ contextKey, model: newModel }),
    isMetadataLoading,
  };
};
